import React from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { EditorSection } from "./institutional/InstitutionalEditorParts";
import { emptyLegalPage } from "../../utils/legalPageContent";

const emptySecao = () => ({ titulo: "", paragrafos: [], itens: [] });

export default function LegalPageEditor({ value, onChange }) {
  const v = value?.format === "legal_page.v1" ? value : emptyLegalPage();
  const set = (patch) => onChange({ ...v, ...patch });
  const setMeta = (patch) => set({ meta: { ...(v.meta || {}), ...patch } });

  const secoes = Array.isArray(v.secoes) ? v.secoes : [];

  return (
    <Box sx={{ mt: 0.5 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Página jurídica em texto estruturado — <strong>sem HTML</strong>. O site monta títulos,
        parágrafos e listas automaticamente.
      </Alert>

      <EditorSection title="Título e descrição (Google)">
        <TextField
          fullWidth
          label="Título da página"
          value={v.meta?.title || ""}
          onChange={(e) => setMeta({ title: e.target.value })}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Descrição curta"
          value={v.meta?.description || ""}
          onChange={(e) => setMeta({ description: e.target.value })}
        />
      </EditorSection>

      <EditorSection title="Introdução">
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Texto de abertura"
          value={v.intro || ""}
          onChange={(e) => set({ intro: e.target.value })}
          placeholder="Parágrafos iniciais. Enter duplo separa parágrafos."
        />
      </EditorSection>

      <EditorSection title="Seções">
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Button
            size="small"
            startIcon={<AddRoundedIcon />}
            onClick={() => set({ secoes: [...secoes, emptySecao()] })}
          >
            Adicionar seção
          </Button>
        </Box>

        {secoes.map((sec, si) => (
          <Paper key={si} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="subtitle2">Seção {si + 1}</Typography>
              <IconButton
                size="small"
                onClick={() => set({ secoes: secoes.filter((_, i) => i !== si) })}
              >
                <DeleteOutlineRoundedIcon fontSize="small" />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              label="Título da seção"
              value={sec.titulo || ""}
              onChange={(e) => {
                const next = [...secoes];
                next[si] = { ...next[si], titulo: e.target.value };
                set({ secoes: next });
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Parágrafos"
              value={(sec.paragrafos || []).join("\n\n")}
              onChange={(e) => {
                const next = [...secoes];
                const paragrafos = e.target.value
                  .split(/\n{2,}/)
                  .map((p) => p.trim())
                  .filter(Boolean);
                next[si] = { ...next[si], paragrafos };
                set({ secoes: next });
              }}
              placeholder="Um parágrafo por bloco — Enter duplo separa parágrafos."
              sx={{ mb: 2 }}
            />

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Itens de lista (opcional)
            </Typography>
            {(sec.itens || []).map((item, ii) => (
              <Box key={ii} sx={{ display: "flex", gap: 1, mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label={`Item ${ii + 1}`}
                  value={item}
                  onChange={(e) => {
                    const next = [...secoes];
                    const itens = [...(next[si].itens || [])];
                    itens[ii] = e.target.value;
                    next[si] = { ...next[si], itens };
                    set({ secoes: next });
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const next = [...secoes];
                    next[si] = {
                      ...next[si],
                      itens: (next[si].itens || []).filter((_, i) => i !== ii),
                    };
                    set({ secoes: next });
                  }}
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<AddRoundedIcon />}
              onClick={() => {
                const next = [...secoes];
                next[si] = { ...next[si], itens: [...(next[si].itens || []), ""] };
                set({ secoes: next });
              }}
            >
              Adicionar item
            </Button>
          </Paper>
        ))}
      </EditorSection>

      <EditorSection title="Rodapé legal">
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Aviso / disclaimer"
          value={v.disclaimer || ""}
          onChange={(e) => set({ disclaimer: e.target.value })}
        />
      </EditorSection>
    </Box>
  );
}
